// IncomePeriodic.js
import {FEM_V, instance_2} from "../../../API/API";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Switch, Table, Tag} from "antd";

import {useDebounce} from "./hooks/useDebounce";
import {SearchHeader} from "./subcomponents/income_periodic/SearchHeader";
import {ObjectNameCell} from "./subcomponents/income_periodic/ObjectNameCell";
import {TypeCell} from "./subcomponents/income_periodic/TypeCell";
import {CostCell} from "./subcomponents/income_periodic/CostCell";
import {NdsCell} from "./subcomponents/income_periodic/NdsCell";
import {StatusCell} from "./subcomponents/income_periodic/StatusCell";
import {BalanceCell} from "./subcomponents/income_periodic/BalanceCell";
import {ActionsCell} from "./subcomponents/income_periodic/ActionsCell";
import {TableSummary} from "./subcomponents/income_periodic/TableSummary";
import {EditModal} from "./subcomponents/income_periodic/EditModal";
import {InfoCircleOutlined} from "@ant-design/icons";
import TablePayments from "./subcomponents/income_periodic/TablePayments";
import ExpensesHeader from "./subcomponents/expenses_header/ExpensesHeader";

const IncomePeriodic = props => {
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(true);
    const [incomeData, setIncomeData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [isShowPayments, setShowPayments] = useState(null);
    const debouncedSearchText = useDebounce(searchText, 300);

    const getIncomePeriodic = useCallback(async () => {
        try {
            setLoading(true);
            const incomeRes = await instance_2.get(`finance/getIncome.php?active=${isActive ? 1 : 0}`)
            if (incomeRes) {
                setIncomeData(incomeRes.data.income.sort((a, b) =>
                    a.obj_name?.localeCompare(b.obj_name || '') || 0
                ));
            }
        } catch (e) {
            console.error('Error fetching income data:', e);
        } finally {
            setLoading(false);
        }
    }, [isActive]);

    useEffect(() => {
        getIncomePeriodic();
    }, [getIncomePeriodic]);

    const handleEdit = useCallback((record) => {
        setCurrentRecord(record);
        setIsModalVisible(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setIsModalVisible(false);
        setCurrentRecord(null);
    }, []);

    const handleSave = async (updatedData) => {
        try {
            const formData = new URLSearchParams();
            formData.append('id', updatedData.id);
            formData.append('type', updatedData.type);
            formData.append('balance', updatedData.balance);
            formData.append('enable', updatedData.enable);
            formData.append('nds', updatedData.nds);
            formData.append('cost', updatedData.cost);
            const response = await instance_2.post('finance/postIncome.php', formData);
        } catch (error) {
            console.error('Error saving data:', error);
        } finally {
            setCurrentRecord(null);
            setIsModalVisible(false);
            getIncomePeriodic();
        }
    };

    const filteredData = useMemo(() => {
        if (!debouncedSearchText) return incomeData;

        return incomeData.filter(item =>
            item.obj_name?.toLowerCase().includes(debouncedSearchText.toLowerCase())
        );
    }, [incomeData, debouncedSearchText]);

    const columns = useMemo(() => [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 30,
            align: 'center',
            render: (id, record) => (
                <span style={{
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: (record.balance || 0) < 0 ? '#cf1322' : '#595959',
                    fontWeight: (record.balance || 0) < 0 ? 600 : 400
                }}>
                    {id}
                </span>
            ),
        },
        {
            title: () => <SearchHeader value={searchText} onChange={setSearchText}/>,
            dataIndex: 'obj_name',
            key: 'obj_name',
            width: 150,
            fixed: 'left',
            render: (text, record) => <ObjectNameCell text={text} balance={record.balance}/>,
        },
        {
            title: 'Форма оплаты',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            filters: [
                {text: 'Наличная', value: 1},
                {text: 'Безналичная', value: 2},
            ],
            onFilter: (value, record) => record.type === value,
            render: (type) => <TypeCell type={type}/>,
        },
        {
            title: 'Стоимость обслуживания',
            key: 'cost',
            align: 'center',
            width: 100,
            render: (_, record) => <CostCell costString={record.cost} nds={record.nds}/>,
        },
        {
            title: 'НДС',
            dataIndex: 'nds',
            key: 'nds',
            width: 60,
            align: 'center',
            render: (nds) => <NdsCell nds={nds}/>,
        },
        {
            title: 'Статус',
            dataIndex: 'enable',
            key: 'enable',
            width: 70,
            align: 'center',
            filters: [
                {text: 'Активные', value: 1},
                {text: 'Неактивные', value: 0},
            ],
            onFilter: (value, record) => record.enable === value,
            render: (enable) => <StatusCell enable={enable}/>,
        },
        {
            title: 'Баланс',
            dataIndex: 'balance',
            key: 'balance',
            width: 80,
            align: 'right',
            sorter: (a, b) => (a.balance || 0) - (b.balance || 0),
            render: (balance) => <BalanceCell balance={balance}/>,
        },
        {
            title: '',
            key: 'actions',
            width: 30,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <ActionsCell record={record} onEdit={handleEdit}/>
            ),
        },
        {
            title: '',
            key: 'payments',
            width: 30,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <InfoCircleOutlined style={{color: '#1890ff'}}
                                    onClick={() => setShowPayments({
                                        id: record.obj_id,
                                        name: record.obj_name,
                                        balance: record.balance
                                    })}
                />
            ),
        },
    ], [searchText, handleEdit]);


    return (
        <div>
            <div style={{margin: '0px auto 10px auto'}}>
                <ExpensesHeader module_v={FEM_V} header_text={'Управление периодическими доходами компании'}/>
            </div>
            <div style={{margin: '5px auto 10px auto'}}>
                <Switch
                    checkedChildren="Активные"
                    unCheckedChildren="Все объекты"
                    defaultChecked
                    onChange={setIsActive}
                />
            </div>


            <Table
                dataSource={filteredData}
                loading={loading}
                columns={columns}
                rowKey="id"
                pagination={false}
                scroll={{x: 1200, y: 600}}
                bordered
                size="small"
                summary={(pageData) => <TableSummary pageData={pageData}/>}
                sticky
                title={() => (
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontSize: '16px', fontWeight: 500}}>
                            Список объектов на обслуживании
                        </span>
                        <Tag color="blue">Всего записей: {filteredData?.length || 0}</Tag>
                    </div>
                )}
            />

            <EditModal
                visible={isModalVisible}
                record={currentRecord}
                onClose={handleModalClose}
                onSave={handleSave}
            />
            <TablePayments
                visible={isShowPayments}
                getIncomePeriodic={getIncomePeriodic}
                id={isShowPayments ? isShowPayments.id : null}
                name={isShowPayments ? isShowPayments.name : ''}
                balance={isShowPayments ? isShowPayments.balance : null}
                onClose={_ => setShowPayments(null)}
                onSave={_ => setShowPayments(null)}
            />
        </div>
    );
};

export default IncomePeriodic;